/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/
import { Button,INodeProps } from '@msdyn365-commerce-modules/utilities';
import classnames from 'classnames';
import * as React from 'react';

export interface IAddContactInfoInput {
    inputRef?: React.RefObject<HTMLInputElement>;
    hasError: boolean;
    email: string;
    hasFirstNameError:boolean;
    hasEmailError:boolean;
    hasLastNameError:boolean;
    hasPhoneNumberError:boolean;
    hasCompanyNameError:boolean;
    inputFirstNameRef?: React.RefObject<HTMLInputElement>;
    inputLastNameRef?: React.RefObject<HTMLInputElement>;
    inputPhoneNumberRef?: React.RefObject<HTMLInputElement>;
    inputCompanyNameRef?: React.RefObject<HTMLInputElement>;
    firstName:string;
    lastName:string;
    phoneNumber:string;
    companyName:string;
    resources: {
        emailLabel: string;
        emailErrortext: string;
        saveBtnLabel: string;
        cancelBtnLabel: string;
        firstNameInputLabel: string;
        firstNameErrortext:string;
        lastNameInputLabel:string;
        lastNameErrortext:string;
        phoneNumberLabel: string;
        phoneNumberErrortext:string;
        companyNameLabel:string;
        companyNameErrortext:string;
        editBtnLabel: string;
    };
    canSubmit: boolean;
    canCancel: boolean;
    onChange(email: string, fName?: string, lName?: string, phone?: string, company?: string): void;
    onSubmit(): void;
    onCancel(): void;
}

export interface IAddContactInfo {
    addFormProps: INodeProps;
    submitButton: React.ReactNode;
    cancelButton: React.ReactNode;
    inputGroupProps: INodeProps;
    label: React.ReactNode;
    error: React.ReactNode;
    input: React.ReactNode;
    emailAddressLabel: React.ReactNode;
    fNameLabel: React.ReactNode;
    lNameLabel: React.ReactNode;
    phoneLabel: React.ReactNode;
    companyNameLabel: React.ReactNode;
    emailError: React.ReactNode;
    fNameError: React.ReactNode;
    lastNameError: React.ReactNode;
    phoneError: React.ReactNode;
    companyNameError: React.ReactNode;
    inputGroupFNameProps: INodeProps;
    fNameInput: React.ReactNode;
    inputGroupLastNameProps: INodeProps;
    lastNameInput: React.ReactNode;
    inputGroupPhoneProps: INodeProps;
    phoneInput: React.ReactNode;
    inputGroupCompanyNameProps: INodeProps;
    companyNameInput: React.ReactNode;
    b2bcompanyNameInput: React.ReactNode;
    b2binput:React.ReactNode;
}

// tslint:disable-next-line:max-func-body-length
const getAddContactInfo = ({
    hasError,
    hasEmailError,
    inputRef,
    email,
    hasFirstNameError,
    hasLastNameError,
    hasPhoneNumberError,
    hasCompanyNameError,
    inputFirstNameRef,
    inputLastNameRef,
    inputPhoneNumberRef,
    inputCompanyNameRef,
    firstName,
    lastName,
    phoneNumber,
    companyName,
    canSubmit,
    canCancel,
    onChange,
    onSubmit,
    onCancel,
    resources: {
        emailLabel, saveBtnLabel, cancelBtnLabel, emailErrortext, firstNameInputLabel, firstNameErrortext, lastNameInputLabel,
        lastNameErrortext, phoneNumberLabel, phoneNumberErrortext, companyNameLabel, companyNameErrortext
    }
}: IAddContactInfoInput): IAddContactInfo => {
    const onInputChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
        const value = e.target.value || '';
        const inputField: HTMLInputElement = e.currentTarget;
        const property = inputField.attributes.getNamedItem('id')?.value.toLocaleLowerCase() || '';
        const changedValues = {'email-address': email, 'first-name': firstName, 'last-name': lastName, 'phone-number': phoneNumber, 'company-name': companyName};
        Object.keys(changedValues).map(key =>  {
            if(key === property) {
                changedValues[key] = value;
            }
          });
        onChange(changedValues['email-address'], changedValues['first-name'], changedValues['last-name'],
                 changedValues['phone-number'],  changedValues['company-name']);
    };

    return {
        addFormProps: { className: 'ms-checkout-guest-profile__add-contact-info' },
        inputGroupProps: { className: classnames('ms-checkout-guest-profile__input', { 'is-invalid': hasError }) },
        inputGroupFNameProps: { className: classnames('ms-checkout-guest-profile__input_FirstName', { 'is-invalid': hasError }) },
        inputGroupLastNameProps: { className: classnames('ms-checkout-guest-profile__input_LastName', { 'is-invalid': hasError }) },
        inputGroupPhoneProps: { className: classnames('ms-checkout-guest-profile__input_PhoneNumber', { 'is-invalid': hasError }) },
        inputGroupCompanyNameProps: { className: classnames('ms-checkout-guest-profile__input_CompanyName', { 'is-invalid': hasError }) },
        emailAddressLabel: labelGroup('ms-checkout-guest-profile__label', 'ms-checkout-guest-profile__input-label', emailLabel),
        fNameLabel: labelGroup('ms-checkout-guest-profile__label_FNameLabel', 'ms-checkout-guest-profile__input-label_FNameLabel', firstNameInputLabel),
        lNameLabel: labelGroup('ms-checkout-guest-profile__label_LastNameLabel', 'ms-checkout-guest-profile__input-label_LastNameLabel', lastNameInputLabel),
        phoneLabel: labelGroup('ms-checkout-guest-profile__label_PhoneLabel', 'ms-checkout-guest-profile__input-label_PhoneLabel', phoneNumberLabel),
        companyNameLabel: labelGroup('ms-checkout-guest-profile__label_CompanyLabel', 'ms-checkout-guest-profile__input-label_CompanyLabel', companyNameLabel),
        emailError: hasError && hasEmailError && errorGroup('ms-checkout-guest-profile__error','ms-checkout-guest-profile__input-error', emailErrortext),
        fNameError: hasError && hasFirstNameError && errorGroup('ms-checkout-guest-profile__error','ms-checkout-guest-profile__input-error',firstNameErrortext),
        lastNameError: hasError && hasLastNameError && errorGroup('ms-checkout-guest-profile__error','ms-checkout-guest-profile__input-error',lastNameErrortext),
        phoneError: hasError && hasPhoneNumberError && errorGroup('ms-checkout-guest-profile__error','ms-checkout-guest-profile__input-error',phoneNumberErrortext),
        companyNameError: hasError && hasCompanyNameError && errorGroup('ms-checkout-guest-profile__error','ms-checkout-guest-profile__input-error',companyNameErrortext),

        submitButton: canSubmit && (
            <Button
                className='ms-checkout-guest-profile__btn-save'
                title={saveBtnLabel}
                color='primary'
                onClick={onSubmit}
            >
            {saveBtnLabel}
            </Button>
        ),
        cancelButton: canCancel && (
            <Button
                className='ms-checkout-guest-profile__btn-cancel'
                title={cancelBtnLabel}
                color='secondary'
                onClick={onCancel}
            >
            {cancelBtnLabel}
            </Button>
        ),
        label: (
            <label id='ms-checkout-guest-profile__label' className='ms-checkout-guest-profile__input-label'>
                {emailLabel}
            </label>
        ),
        error: hasError && hasEmailError && (
            <span
                id='ms-checkout-guest-profile__error'
                className='ms-checkout-guest-profile__input-error'
                role='alert'
                aria-live='assertive'
            >
                {emailErrortext}
            </span>
        ),
        input: (
            <input
                ref={inputRef}
                id={'email-address'}
                type='text'
                className='ms-checkout-guest-profile__input-text form-control ms-checkout-guest-profile_email'
                aria-label={emailLabel}
                onChange={onInputChange}
                value={email}
                aria-labelledby='ms-checkout-guest-profile__label ms-checkout-guest-profile__error'
            />),
        b2binput: (
            <input
                ref={inputRef}
                id={'email-address'}
                type='text'
                className='ms-checkout-guest-profile__input-text form-control ms-checkout-guest-profile_email'
                aria-label={emailLabel}
                onChange={onInputChange}
                value={email}
                aria-labelledby='ms-checkout-guest-profile__label ms-checkout-guest-profile__error'
                readOnly
            />),
        fNameInput: (
                <input
                    ref={inputFirstNameRef}
                    type='text'
                    id={'first-name'}
                    className='ms-checkout-guest-profile__input-text form-control'
                    aria-label={firstNameInputLabel}
                    onChange={onInputChange}
                    value={firstName}
                    aria-labelledby='checkout-guest-profile__label checkout-guest-profile__error ms-checkout-guest-profile__label_FNameLabel'
                />
            ),
        lastNameInput: (
                <input
                    ref={inputLastNameRef}
                    type='text'
                    id={'last-name'}
                    className='ms-checkout-guest-profile__input-text form-control'
                    aria-label={lastNameInputLabel}
                    onChange={onInputChange}
                    value={lastName}
                    aria-labelledby='checkout-guest-profile__label checkout-guest-profile__error ms-checkout-guest-profile__label_LastNameLabel'
                />
            ),
        phoneInput: (
                <input
                    ref={inputPhoneNumberRef}
                    type='text'
                    id={'phone-number'}
                    className='ms-checkout-guest-profile__input-text form-control'
                    aria-label={phoneNumberLabel}
                    onChange={onInputChange}
                    value={phoneNumber}
                    aria-labelledby='checkout-guest-profile__label checkout-guest-profile__error ms-checkout-guest-profile__label_PhoneLabel '
                />
            ),
        companyNameInput: (
                <input
                    ref={inputCompanyNameRef}
                    type='text'
                    id={'company-name'}
                    className='ms-checkout-guest-profile__input-text form-control'
                    aria-label={companyNameLabel}
                    onChange={onInputChange}
                    value={companyName}
                    aria-labelledby='checkout-guest-profile__label checkout-guest-profile__error ms-checkout-guest-profile__label_CompanyLabel'
                />
            ),
        b2bcompanyNameInput: (
            <input
                ref={inputCompanyNameRef}
                type='text'
                id={'company-name'}
                className='ms-checkout-guest-profile__input-text form-control'
                aria-label={companyNameLabel}
                onChange={onInputChange}
                value={companyName}
                aria-labelledby='checkout-guest-profile__label checkout-guest-profile__error ms-checkout-guest-profile__label_CompanyLabel'
                readOnly
            />
        )
    };
};

const labelGroup = (id: string, className: string, value:string): JSX.Element => {
    return (
        <label id={id} className={className}>
         {value}
        </label>
     );
};

const errorGroup = (id: string, className: string, value:string): JSX.Element => {
    return (
        <span
            id={id}
            className={className}
            role='alert'
            aria-live='assertive'
        >
            {value}
        </span>
     );
};

export default getAddContactInfo;
